import React, { useState, useEffect } from 'react';
import {
  X,
  Star,
  Zap,
  ShoppingBag,
  Heart,
  Download,
  ShieldCheck,
  CheckCircle2,
  ZoomIn,
  ZoomOut,
  Layers,
  FileCheck2,
  Sparkles,
  Share2,
  Tag,
  MessageSquare,
  Box,
  Rotate3d,
  RefreshCw,
  Eye
} from 'lucide-react';
import { Product, Currency, SoftwareFormat, DocumentCategory, ReviewItem } from '../types';
import { getCategorySlug, getProductSlug } from '../lib/slugUtils';
import { FormattedDescription } from './FormattedDescription';
import { getSafeProductImages, getSafeProductFirstImage, getSafeFormatProductImages, getImageDeduplicationKey } from '../lib/productImages';
import { saveReviewToFirestore } from '../lib/reviewsService';

interface ProductDetailModalProps {
  product: Product | null;
  isLoading?: boolean;
  onClose: () => void;
  currency: Currency;
  isInWishlist: boolean;
  onToggleWishlist: (p: Product) => void;
  onAddToCart: (p: Product, format?: SoftwareFormat) => void;
  onQuickBuy?: (product: Product, format?: any) => void;
  darkMode: boolean;
  allProducts?: Product[];
  onSelectProduct?: (p: Product) => void;
  onNavigateCategory?: (category: DocumentCategory) => void;
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  isLoading,
  onClose,
  currency,
  isInWishlist,
  onToggleWishlist,
  onAddToCart,
  onQuickBuy,
  darkMode,
  allProducts = [],
  onSelectProduct,
  onNavigateCategory,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState<SoftwareFormat>(product?.formats?.[0] || 'PSD');
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  // Review submission state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewAuthor, setReviewAuthor] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  useEffect(() => {
    if (product) {
      setSelectedImageIndex(0);
      setZoomLevel(1);
      setSelectedFormat(product?.formats?.[0] || 'PSD');
      setReviewSubmitted(false);
    }
  }, [product?.id, product?.updatedAt]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product || !reviewAuthor.trim() || !reviewComment.trim()) return;
    setIsSubmittingReview(true);
    try {
      const newReview: ReviewItem = {
        id: `rev_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        productId: product.id,
        userName: reviewAuthor.trim(),
        rating: reviewRating,
        comment: reviewComment.trim(),
        createdAt: new Date().toISOString(),
        status: 'approved'
      };
      await saveReviewToFirestore(newReview);
      setReviewSubmitted(true);
      setShowReviewForm(false);
      setReviewAuthor('');
      setReviewComment('');
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // 3D Perspective Stage States
  const [is3dMode, setIs3dMode] = useState(true);
  const [preset3d, setPreset3d] = useState<'isometric' | 'float' | 'front' | 'custom'>('isometric');
  const [rotX, setRotX] = useState(12);
  const [rotY, setRotY] = useState(-16);
  const [isHovered, setIsHovered] = useState(false);
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50 });

  const handleMouseMove3D = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!is3dMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Calculate 3D tilt angles (-22 to +22 deg)
    const calcX = -((y - centerY) / centerY) * 20;
    const calcY = ((x - centerX) / centerX) * 24;
    
    setRotX(calcX);
    setRotY(calcY);
    setGlarePos({ x: Math.round((x / rect.width) * 100), y: Math.round((y / rect.height) * 100) });
    setPreset3d('custom');
  };

  const handleTouchMove3D = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!is3dMode || !e.touches[0]) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const y = e.touches[0].clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const calcX = -((y - centerY) / centerY) * 20;
    const calcY = ((x - centerX) / centerX) * 24;
    
    setRotX(calcX);
    setRotY(calcY);
    setGlarePos({ x: Math.round((x / rect.width) * 100), y: Math.round((y / rect.height) * 100) });
    setPreset3d('custom');
  };

  const reset3DView = (preset: 'isometric' | 'float' | 'front' = 'isometric') => {
    setPreset3d(preset);
    if (preset === 'isometric') { setRotX(12); setRotY(-16); }
    else if (preset === 'float') { setRotX(20); setRotY(0); }
    else if (preset === 'front') { setRotX(0); setRotY(0); }
    setGlarePos({ x: 50, y: 50 });
  };

  const relatedProducts = React.useMemo(() => {
    if (!product || !allProducts || allProducts.length === 0) return [];
    return allProducts.filter(p => p.id !== product.id && (p.category === product.category || (p.country && p.country === product.country))).slice(0, 3);
  }, [allProducts, product]);

  const safeImages = React.useMemo(() => {
    if (!product) return [];
    const raw = getSafeFormatProductImages(product, selectedFormat);
    const seenKeys = new Set<string>();
    const deduplicated: string[] = [];
    for (const img of raw) {
      if (!img || typeof img !== 'string') continue;
      const key = getImageDeduplicationKey(img);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        deduplicated.push(img);
      }
    }
    return deduplicated.length > 0 ? deduplicated : raw;
  }, [product, selectedFormat]);

  if (!product && !isLoading) return null;
  
  if (isLoading && !product) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-950/95 ">
        <div className={`relative w-full max-w-5xl rounded-3xl border shadow-2xl overflow-hidden my-auto min-h-[600px] ${darkMode ? 'glass-card border-white/20' : 'glass-card-light border-slate-200'}`}>
          <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2.5 rounded-full bg-slate-900/95 hover:bg-rose-500 text-white transition-all">
             <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col lg:flex-row h-full">
            <div className="w-full lg:w-3/5 p-6 animate-pulse">
               <div className="w-full h-80 bg-slate-800/50 rounded-2xl mb-4"></div>
               <div className="flex gap-4">
                  <div className="w-20 h-16 bg-slate-800/50 rounded-xl"></div>
                  <div className="w-20 h-16 bg-slate-800/50 rounded-xl"></div>
               </div>
            </div>
            <div className="w-full lg:w-2/5 p-6 border-l border-slate-800/50 animate-pulse flex flex-col gap-6">
               <div className="w-32 h-6 bg-slate-800/50 rounded-full"></div>
               <div className="w-full h-10 bg-slate-800/50 rounded-xl"></div>
               <div className="w-24 h-8 bg-slate-800/50 rounded-xl"></div>
               <div className="w-full h-24 bg-slate-800/50 rounded-xl"></div>
               <div className="w-full h-14 bg-slate-800/50 rounded-xl mt-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const currencySymbols: Record<Currency, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    };

  const exchangeRates: Record<Currency, number> = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.78,
    };

  const symbol = currencySymbols[currency];
  const rate = exchangeRates[currency];

  const priceFormatted = (product.price * rate).toFixed(2);
  const origPriceFormatted = (product.originalPrice * rate).toFixed(2);

  const productSlug = getProductSlug(product);
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/product/${productSlug}`
    : `https://rheditography.net/product/${productSlug}`;

  const handleShare = () => {
    try {
      navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch (e) {
      console.warn(e);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-950/95 ">
      
      {/* Modal Card Box */}
      <div className={`relative w-full max-w-5xl rounded-3xl border shadow-2xl overflow-hidden my-auto ${
        darkMode ? 'glass-card border-white/20 text-white' : 'glass-card-light border-slate-200 text-slate-900'
      }`}>
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2.5 rounded-full bg-slate-900/95 hover:bg-rose-500 text-white transition-all shadow-xl"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid lg:grid-cols-12 gap-6 p-6 sm:p-8 max-h-[88vh] overflow-y-auto custom-scrollbar">
          
          {/* Left Column: Interactive Image Gallery & Preview Stage */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            
            {/* Main Interactive Stage */}
            <div
              className="relative rounded-2xl overflow-hidden border border-white/15 aspect-[4/3] bg-slate-950 flex items-center justify-center group select-none p-4 shadow-2xl transition-all duration-300"
            >
              {/* Card Frame Box */}
              <div
                style={{
                  transform: `scale(${zoomLevel})`,
                  transition: 'transform 0.3s ease-out',
                }}
                className="relative w-full h-full rounded-xl overflow-hidden bg-slate-900 border border-white/20 transition-all duration-300 flex items-center justify-center"
              >
                <img
                  src={safeImages[selectedImageIndex] || safeImages[0]}
                  alt={`${product.title} - ${product.category} Vector PSD Template - RH Editography`}
                  title={`${product.title} High Resolution Preview ${selectedImageIndex + 1}`}
                  
                  
                  
                  className="w-full h-full object-contain pointer-events-none"
                />

                {/* Watermark Overlay */}
                {watermarkEnabled && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                    <p className="text-3xl sm:text-5xl font-black text-white rotate-[-30deg] tracking-widest uppercase border-4 border-white p-3">
                      RH EDITOGRAPHY
                    </p>
                  </div>
                )}
              </div>

              {/* Top Controls: Zoom & Watermark */}
              <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2 flex-wrap pointer-events-auto z-10">
                <div className="flex items-center gap-1.5 bg-slate-900/90  p-1 rounded-xl border border-white/15 shadow-lg">
                  <button
                    onClick={() => setZoomLevel(prev => Math.min(prev + 0.25, 2))}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setZoomLevel(prev => Math.max(prev - 0.25, 0.8))}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white cursor-pointer"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setWatermarkEnabled(!watermarkEnabled)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold border cursor-pointer transition-colors ${
                      watermarkEnabled ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' : 'bg-slate-800 text-slate-400 border-white/10'
                    }`}
                  >
                    {watermarkEnabled ? 'Watermark ON' : 'OFF'}
                  </button>
                </div>
              </div>

              {/* Bottom Badge: Format */}
              <div className="absolute bottom-3 right-3 flex items-center gap-2 pointer-events-none z-10">
                <div className="px-3 py-1 rounded-xl bg-slate-900/90 text-cyan-400 text-xs font-mono font-bold border border-cyan-500/30  shadow-md">
                  Format: .{selectedFormat}
                </div>
              </div>
            </div>

            {/* Thumbnail Selectors */}
            <div className="flex items-center gap-3 overflow-x-auto pb-1">
              {safeImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedImageIndex(idx);
                    setZoomLevel(1);
                  }}
                  className={`relative w-20 h-16 rounded-xl overflow-hidden border-2 flex-shrink-0 transition-all ${
                    selectedImageIndex === idx ? 'border-cyan-400 scale-105 shadow-md' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img}
                    alt={`${product.title} Thumbnail ${idx + 1}`}
                    title={`${product.title} Preview ${idx + 1}`}
                    
                    
                    
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Specs Table */}
            <div className="p-4 rounded-2xl bg-slate-900/50 border border-white/10 space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" />
                <span>3D Technical Specifications</span>
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-slate-400">Resolution:</p>
                  <p className="font-bold">{product.specs?.resolution || '6000 x 4000 px'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Color Space:</p>
                  <p className="font-bold">{product.specs?.colorMode || 'RGB / CMYK'}</p>
                </div>
                <div>
                  <p className="text-slate-400">DPI Quality:</p>
                  <p className="font-bold text-emerald-400">{product.specs?.dpi || 300} DPI</p>
                </div>
                <div>
                  <p className="text-slate-400">Layer Setup:</p>
                  <p className="font-bold">{product.specs?.layers || 'Organized Smart Objects'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Fonts Included:</p>
                  <p className="font-bold text-cyan-400">{product.specs?.fontsIncluded ? 'Yes (Free Commercial)' : 'Yes'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Archive Size:</p>
                  <p className="font-bold">{product.specs?.fileSize || 'Smart Layer Archive'}</p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Template Info & Purchasing */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            
            <div className="space-y-4">
              {/* Category & Ratings */}
              <div className="flex items-center justify-between">
                <span
                  onClick={() => {
                    if (onNavigateCategory) {
                      onClose();
                      onNavigateCategory(product.category);
                    }
                  }}
                  className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-extrabold uppercase cursor-pointer hover:bg-blue-500/30 transition-all"
                  title={`View all ${product.category} templates`}
                >
                  {product.category}
                </span>
                <div className="flex items-center gap-2 text-amber-400 font-bold text-sm flex-wrap">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-400" />
                    <span>{product.rating || 5.0}</span>
                    <span className="text-slate-400 font-normal">({product.reviewCount || 12} reviews)</span>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-black leading-snug">
                {product.title}
              </h2>

              {/* Description */}
              <div className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                <FormattedDescription text={product.description || ''} darkMode={darkMode} />
              </div>

              {/* Key Features List */}
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Included Features:</p>
                <ul className="space-y-1.5 text-xs font-medium">
                  {(product.detailedFeatures || ['Fully Editable Vector PSD Layers', 'Smart Object Photo Replacement', 'High Resolution Print Ready', 'Free Commercial Fonts Included']).map((feat, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Software Format Selection */}
              <div className="space-y-2 pt-2">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Select File Format:</p>
                <div className="flex flex-wrap gap-2">
                  {(product.formats || ['PSD', 'AI']).map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => setSelectedFormat(fmt)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                        selectedFormat === fmt
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-400 shadow-md shadow-blue-500/20'
                          : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      .{fmt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price & Discount Panel */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-950/40 via-indigo-950/40 to-slate-950/60 border border-blue-500/20 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">Master License Price</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-gradient">
                      {symbol}{priceFormatted}
                    </span>
                    <span className="text-sm line-through text-slate-500 font-bold">
                      {symbol}{origPriceFormatted}
                    </span>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-extrabold">
                  Save {product.discountPercent}%
                </span>
              </div>
            </div>

            {/* Action Buttons Footer */}
            <div className="space-y-3 pt-2">
              <div className="flex gap-2 w-full">
                <button
                  onClick={() => {
                    onAddToCart(product, selectedFormat);
                    onClose();
                  }}
                  className="flex-1 py-3.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-sm flex items-center justify-center gap-2 transition-all duration-200 border border-slate-700 hover:border-slate-600"
                >
                  <ShoppingBag className="w-5 h-5" />
                  <span>Add to Cart</span>
                </button>
                
                {onQuickBuy && (
                  <button
                    onClick={() => {
                      onQuickBuy(product, selectedFormat);
                      onClose();
                    }}
                    className="flex-1 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-400 text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/20 hover:shadow-teal-500/40 hover:scale-[1.02] active:scale-98 transition-all duration-200"
                  >
                    <Zap className="w-5 h-5" />
                    <span>Quick Buy</span>
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => onToggleWishlist(product)}
                  className={`flex-1 py-2.5 px-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                    isInWishlist ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isInWishlist ? 'fill-rose-400 text-rose-400' : ''}`} />
                  <span>{isInWishlist ? 'Saved' : 'Wishlist'}</span>
                </button>

                <button
                  onClick={handleShare}
                  className="py-2.5 px-3.5 rounded-xl bg-slate-900/60 border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs font-bold flex items-center gap-1.5 transition-all"
                  title="Copy Direct Product Page Link"
                >
                  <Share2 className="w-4 h-4" />
                  <span>{copiedLink ? 'Link Copied!' : 'Copy Link'}</span>
                </button>

                {/* Social Share Shortcuts */}
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${product.title} - RH Editography:\n${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-emerald-600/20 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center group"
                  title="Share on WhatsApp"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                  </svg>
                </a>

                <a
                  href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(product.title + ' - RH Editography Template')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-sky-600/20 text-sky-400 border border-sky-500/40 hover:bg-sky-600 hover:text-white transition-all flex items-center justify-center group"
                  title="Share on Telegram"
                >
                  <svg className="w-4 h-4 fill-current ml-[1px]" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.94z"/>
                  </svg>
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/40 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center group"
                  title="Share on Facebook"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>

                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(product.title + ' - RH Editography Template')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700 hover:text-white transition-all flex items-center justify-center group"
                  title="Share on X (Twitter)"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>

                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-xl bg-indigo-600/20 text-indigo-400 border border-indigo-500/40 hover:bg-indigo-600 hover:text-white transition-all flex items-center justify-center group"
                  title="Share on LinkedIn"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>


            </div>

          </div>

        </div>

        {/* Related Products Row */}
        {relatedProducts.length > 0 && (
          <div className="pt-6 border-t border-white/10 mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-extrabold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" />
                <span>Related Templates</span>
              </h4>
              <span className="text-xs text-slate-400 font-medium">{relatedProducts.length} similar items</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {relatedProducts.map((relP, idx) => (
                <div
                  key={`${relP.id}-${idx}`}
                  onClick={() => {
                    if (onSelectProduct) {
                      onSelectProduct(relP);
                    }
                  }}
                  className="p-3 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-cyan-500/50 cursor-pointer transition-all flex items-center gap-3 group"
                >
                  <img
                    src={getSafeProductFirstImage(relP)}
                    alt={relP.title}
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0 group-hover:scale-105 transition-transform"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold truncate group-hover:text-cyan-300 transition-colors">{relP.title}</p>
                    <p className="text-[10px] text-cyan-400 font-extrabold mt-0.5">${relP.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

    </div>
  );
};
